<template>
  <div
    v-if="shouldShow"
    class="maintenance-scheduled-banner"
    :class="{
      'maintenance-scheduled-banner--underway': underway,
    }"
  >
    <div
      class="maintenance-scheduled-banner__container"
      :class="{
        'maintenance-scheduled-banner__container--scheduled': !underway,
      }"
    >
      <div class="maintenance-scheduled-banner__wrapper">
        <div class="maintenance-scheduled-banner__title">
          <AlertTriangleIcon v-if="underway" class="maintenance-scheduled-banner__title-icon" />
          <AlertCircleIcon v-else class="maintenance-scheduled-banner__title-icon" />
          <span class="maintenance-scheduled-banner__title-text">
            {{ title }}
          </span>
        </div>
        <div
          class="maintenance-scheduled-banner__separator"
          :class="{
            'maintenance-scheduled-banner__separator--underway': underway,
          }"
        ></div>
        <div
          class="maintenance-scheduled-banner__description"
          :class="{
            'maintenance-scheduled-banner__description--underway': underway,
          }"
        >
          <template v-if="underway">
            The platform is temporarily unavailable while we complete a system update. Estimated
            completion time is less than 30 minutes. This message will disappear once the update is
            finished.
          </template>
          <template v-else>
            INE will undergo scheduled maintenance on <b>{{ maintenanceDateTime }}</b> for
            approximately 30 minutes. During this time, the platform will be unavailable. Thank you
            for your patience.
          </template>
        </div>
      </div>
      <XIcon
        v-if="!underway"
        class="maintenance-scheduled-banner__close-icon"
        @click="closeMaintenanceBanner(maintenanceScheduled)"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import { formatInTimeZone } from 'date-fns-tz'
import XIcon from 'vue-feather-icons/icons/XIcon'
import AlertCircleIcon from 'vue-feather-icons/icons/AlertCircleIcon'
import AlertTriangleIcon from 'vue-feather-icons/icons/AlertTriangleIcon'
import {
  getMaintenanceScheduledDate,
  isMaintenanceUnderway,
  shouldShowMaintenanceBanner,
} from '@/helpers/maintenanceBanner'

export default {
  name: 'MaintenanceScheduledBanner',
  components: {
    AlertCircleIcon,
    XIcon,
    AlertTriangleIcon,
  },
  props: {
    forceUnderway: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  computed: {
    ...mapState({
      maintenanceBannerClosedFor: state => state.app.maintenanceBannerClosedFor,
    }),
    ...mapGetters({
      maintenanceScheduled: 'featureFlags/maintenanceScheduled',
      maintenanceOnGoing: 'featureFlags/maintenanceOnGoing',
    }),
    scheduledDate() {
      return getMaintenanceScheduledDate(this.maintenanceScheduled)
    },
    shouldShow() {
      return shouldShowMaintenanceBanner({
        maintenanceScheduled: this.maintenanceScheduled,
        maintenanceOnGoing: this.maintenanceOnGoing,
        maintenanceBannerClosedFor: this.maintenanceBannerClosedFor,
        forceUnderway: this.forceUnderway,
      })
    },
    underway() {
      return isMaintenanceUnderway({
        maintenanceScheduled: this.maintenanceScheduled,
        maintenanceOnGoing: this.maintenanceOnGoing,
        forceUnderway: this.forceUnderway,
      })
    },
    title() {
      return this.underway ? 'Maintenance Underway' : 'Scheduled Maintenance'
    },
    description() {
      return this.underway
        ? 'Estimated time to completion is less than 30 minutes. <b>Please avoid starting or continuing exams until the update is complete.</b> Some services may be temporarily impacted. This message will disappear once maintenance is complete.'
        : `INE will undergo scheduled maintenance on
          <b>${this.maintenanceDateTime}</b> for approximately 30 minutes. <b>We
          strongly recommend not starting or continuing any exams during this
          time.</b> Some services may be temporarily impacted. Thank you for your
          patience.`
    },
    maintenanceDateTime() {
      if (!this.scheduledDate) return ''
      return formatInTimeZone(
        this.scheduledDate,
        'America/New_York',
        "MMMM do, 'at' h:mm a zzz"
      ).replace(/\s*(\w\w)\s*(EST|EDT)$/, (_, ampm, tz) => `${ampm.toLowerCase()} ${tz}`)
    },
  },

  methods: {
    ...mapActions('app', ['closeMaintenanceBanner']),
  },
}
</script>

<style lang="scss" scoped>
.maintenance-scheduled-banner {
  padding: 10px;

  display: flex;
  justify-content: center;
  align-items: center;

  background: var(--orange-horizontal-gradient);
  z-index: 99;

  &--underway {
    background: linear-gradient(270deg, rgba(239, 34, 34, 0) 61.35%, #ef2222 98.03%), #b20000;
  }
  &__container {
    display: flex;
    justify-content: center;
    align-items: center;

    &--scheduled {
      padding-right: 30px;
    }

    @include mobile {
      padding: 0;
      width: 80%;
    }
    @include desktop {
      width: 60%;
    }
  }

  &__wrapper {
    display: flex;
    align-items: center;

    @include mobile {
      gap: 10px;
    }

    @include tablet {
      gap: 20px;
    }
  }
  &__title {
    display: none;

    @include mobile {
      display: flex;
      align-items: center;
      gap: 7px;

      color: white;

      font-size: 14px;
      line-height: 17px;
    }
    @include desktop {
      font-size: 18px;
      line-height: 24px;
      text-wrap: nowrap;
    }

    &-icon {
      width: 24px;
      height: 24px;
    }
  }

  &__separator {
    display: none;

    @include mobile {
      display: block;

      width: 1px;
      height: 41px;
      background: var(--orange-light);

      &--underway {
        background: var(--red-light);
      }
    }
  }
  &__description {
    font-size: 10px;
    font-style: normal;
    font-weight: 500;
    line-height: 12px;
    color: var(--dusty-gray);

    @include mobile {
      font-size: 12px;
      line-height: 14px;
    }
    @include desktop {
      font-size: 13px;
      line-height: 17px;
    }
  }
  &__close-icon {
    position: absolute;
    right: 12px;
    color: white;
    cursor: pointer;
    @include mobile {
      right: 20px;
    }
  }
}
</style>
